import { watch } from "vue";
import * as Sentry from "@sentry/vue";

import gaiupApolloClient from "@/graphql/services/ApolloClient";
import { useModalStore } from "@/stores/modals/modals.store";
import { featureFlags } from "@/services/featureFlags";

import GetUserWelcomeFlowQuery from "@/graphql/queries/GetUserWelcomeFlowQuery";
import GetUserWelcomeFlowRowsQuery from "@/graphql/queries/GetUserWelcomeFlowRowsQuery";
import InsertUserWelcomeFlowMutation from "@/graphql/mutations/InsertUserWelcomeFlowMutation";
import UpdateUserWelcomeFlowCompletedMutation from "@/graphql/mutations/UpdateUserWelcomeFlowCompletedMutation";
import InsertUserWelcomeFlowEventMutation from "@/graphql/mutations/InsertUserWelcomeFlowEventMutation";

import type {
  GetUserWelcomeFlowQuery as GetUserWelcomeFlowQueryResult,
  GetUserWelcomeFlowQueryVariables,
  GetUserWelcomeFlowRowsQuery as GetUserWelcomeFlowRowsQueryResult,
  GetUserWelcomeFlowRowsQueryVariables,
  InsertUserWelcomeFlowMutation as InsertUserWelcomeFlowMutationResult,
  InsertUserWelcomeFlowMutationVariables,
  UpdateUserWelcomeFlowCompletedMutation as UpdateUserWelcomeFlowCompletedMutationResult,
  UpdateUserWelcomeFlowCompletedMutationVariables,
  InsertUserWelcomeFlowEventMutation as InsertUserWelcomeFlowEventMutationResult,
  InsertUserWelcomeFlowEventMutationVariables,
} from "@/graphql/generated/graphql";
import type { WelcomeVariant, WelcomeFlowEventKind, WelcomeStepReached } from "./types/WelcomeFlowTypes";

// Shared by every welcome-tour flow — `user_welcome_flow` is keyed `(user, flowKey)`.

export type WelcomeFlowRow = { variant: WelcomeVariant; seenAt: string; completedAt: string | null };

/** The `user_welcome_flow` row for this flow key, or null when the user hasn't seen it yet. */
export async function getWelcomeFlowRow(flowKey: string): Promise<WelcomeFlowRow | null> {
  const result = await gaiupApolloClient.query<GetUserWelcomeFlowQueryResult, GetUserWelcomeFlowQueryVariables>({
    query: GetUserWelcomeFlowQuery,
    variables: { flowKey },
    fetchPolicy: "network-only",
    context: { tolerateMissingSchema: true },
  });
  const row = result.data?.userWelcomeFlow?.[0];
  return row ? toWelcomeFlowRow(row) : null;
}

/** The rows for several flow keys in one read, keyed by flow key; a key the user has never seen is absent. */
export async function getWelcomeFlowRows(flowKeys: readonly string[]): Promise<Map<string, WelcomeFlowRow>> {
  const result = await gaiupApolloClient.query<GetUserWelcomeFlowRowsQueryResult, GetUserWelcomeFlowRowsQueryVariables>({
    query: GetUserWelcomeFlowRowsQuery,
    variables: { flowKeys: [...flowKeys] },
    fetchPolicy: "network-only",
    context: { tolerateMissingSchema: true },
  });
  return new Map((result.data?.userWelcomeFlow ?? []).map((row) => [row.flowKey, toWelcomeFlowRow(row)]));
}

function toWelcomeFlowRow(row: { variant: string; seenAt: string; completedAt?: string | null }): WelcomeFlowRow {
  return { variant: row.variant as WelcomeVariant, seenAt: row.seenAt, completedAt: row.completedAt ?? null };
}

/**
 * Something failed and we carried on. Every write here is deliberately non-blocking — a lost
 * analytics row must not break a tour — but that also means a refused permission or a schema drift
 * looks exactly like success, so nothing exercised on dev can tell you the write works. Production
 * behaviour is unchanged; a dev build additionally says it out loud, where someone walking the flow
 * is actually looking.
 *
 * Lives here because these are its only callers. Move it to a service if a third feature wants it.
 */
export function reportSwallowedFailure(operation: string, detail: unknown, extra?: Record<string, unknown>): void {
  if (detail instanceof Error) {
    // Two shapes rather than passing `undefined`: callers without context keep the exact one-argument
    // call they had before this helper existed.
    if (extra) Sentry.captureException(detail, { extra });
    else Sentry.captureException(detail);
  } else {
    Sentry.captureMessage(`Welcome flow: ${operation} — ${String(detail)}`);
  }

  if (featureFlags.isDev) {
    console.error(`[welcome flow] ${operation} failed, and production would swallow this:`, detail, extra ?? "");
  }
}

/**
 * Whether the automatic run is finished with this flow.
 *
 * A MANDATORY deck counts only once it has been completed. The row is written when the tour opens,
 * so gating on the row alone let a refresh on step two end the tour for good, and everyone is meant
 * to go through the whole thing. Re-opening is safe: the insert's `on_conflict` updates no columns,
 * so `seenAt` keeps the first sighting.
 *
 * A skippable deck counts as soon as it has been seen, or its Skip button would not survive a
 * reload.
 */
export async function isWelcomeFlowDone(flowKey: string, mandatory: boolean): Promise<boolean> {
  const row = await getWelcomeFlowRow(flowKey);
  if (!row) return false;
  return mandatory ? row.completedAt !== null : true;
}

/** Best-effort: a repeat run of the tour is better than never showing it, so a failure is swallowed and reported. */
export async function insertWelcomeFlowRow(flowKey: string, variant: WelcomeVariant): Promise<void> {
  try {
    await gaiupApolloClient.mutate<InsertUserWelcomeFlowMutationResult, InsertUserWelcomeFlowMutationVariables>({
      mutation: InsertUserWelcomeFlowMutation,
      variables: { flowKey, variant },
      context: { tolerateMissingSchema: true },
    });
  } catch (error) {
    reportSwallowedFailure("insertWelcomeFlowRow", error);
  }
}

/** Best-effort: skipping the completed write leaves completedAt null (completed vs abandoned stays visible in the data). */
export async function markWelcomeFlowCompleted(flowKey: string): Promise<void> {
  try {
    const result = await gaiupApolloClient.mutate<
      UpdateUserWelcomeFlowCompletedMutationResult,
      UpdateUserWelcomeFlowCompletedMutationVariables
    >({
      mutation: UpdateUserWelcomeFlowCompletedMutation,
      variables: { flowKey },
      context: { tolerateMissingSchema: true },
    });

    // Zero rows is a silent failure otherwise: a wrong flow key, or no update permission on the
    // table for this role. It leaves completedAt null, which everything downstream reads as "never
    // finished" — including the platform review card, which waits on it.
    if (result.data?.update_userWelcomeFlow?.affected_rows === 0) {
      reportSwallowedFailure("markWelcomeFlowCompleted", `completion update matched no rows for ${flowKey}`);
    }
  } catch (error) {
    reportSwallowedFailure("markWelcomeFlowCompleted", error);
  }
}

/**
 * Append-only: one row per welcome-tour lifecycle moment (opened/completed/abandoned/replayed).
 * Written, never read — reporting completion, drop-off and replay counts per deck is aggregate SQL
 * against `user_welcome_flow_event`, not a frontend read. Best-effort, like insertWelcomeFlowRow.
 */
export async function recordWelcomeFlowEvent(input: {
  flowKey: string;
  event: WelcomeFlowEventKind;
  variant?: WelcomeVariant;
  stepReached?: WelcomeStepReached | null;
}): Promise<void> {
  // Production only. Dev and local runs are the team testing, not users, and would drown the numbers.
  if (featureFlags.isDev) return;
  try {
    await gaiupApolloClient.mutate<InsertUserWelcomeFlowEventMutationResult, InsertUserWelcomeFlowEventMutationVariables>({
      mutation: InsertUserWelcomeFlowEventMutation,
      variables: {
        flowKey: input.flowKey,
        event: input.event,
        variant: input.variant,
        stepIndex: input.stepReached?.stepIndex ?? null,
        stepKey: input.stepReached?.stepKey ?? null,
      },
      context: { tolerateMissingSchema: true },
    });
  } catch (error) {
    reportSwallowedFailure("recordWelcomeFlowEvent", error);
  }
}

/** Resolves once no modal is open — a welcome tour must never open underneath one. */
export async function waitForModalStackToEmpty(): Promise<void> {
  const modals = useModalStore();
  if (modals.stack.length === 0) return;

  await new Promise<void>((resolve) => {
    const stop = watch(
      () => modals.stack.length,
      (length) => {
        if (length === 0) {
          stop();
          resolve();
        }
      }
    );
  });
}
